import { type VariantProps, cva } from 'class-variance-authority';
import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';

/**
 * Chip primitive — small status / metadata pills. Used for platform
 * connection chips, suggestion chips above the prompt, status pills
 * (`DRAFT`, `LIVE`, `SOLD`), the credit pill in the top header, and
 * filter chips on the Gallery toolbar.
 *
 * Variants:
 * - `solid` — filled secondary surface.
 * - `soft` — translucent accent (credit pill, "live on etsy" markers,
 *   selected filter chips).
 * - `outline` — transparent with a 1px border, used inline in tables.
 *
 * Tone biases the soft variant only — `tone='accent'` is the brand blue,
 * `tone='success' | 'warning' | 'danger'` borrow from semantic tokens.
 */
const chipVariants = cva(
  'inline-flex items-center gap-1 rounded-pill text-xs font-medium leading-none transition-colors duration-200 ease-out',
  {
    variants: {
      variant: {
        solid: 'bg-surface-2 text-fg border border-border',
        soft: 'border border-transparent',
        outline: 'bg-transparent text-fg-2 border border-border',
      },
      tone: {
        neutral: '',
        accent: '',
        success: '',
        warning: '',
        danger: '',
      },
      size: {
        xs: 'h-5 px-2',
        sm: 'h-6 px-2.5',
        md: 'h-7 px-3',
      },
    },
    compoundVariants: [
      { variant: 'soft', tone: 'neutral', className: 'bg-surface-2 text-fg-2' },
      { variant: 'soft', tone: 'accent', className: 'bg-brand/15 text-brand' },
      { variant: 'soft', tone: 'success', className: 'bg-success/15 text-success' },
      { variant: 'soft', tone: 'warning', className: 'bg-warning/20 text-warning' },
      { variant: 'soft', tone: 'danger', className: 'bg-danger/15 text-danger' },
      { variant: 'outline', tone: 'accent', className: 'border-brand/40 text-brand' },
      { variant: 'outline', tone: 'success', className: 'border-success/40 text-success' },
      { variant: 'outline', tone: 'warning', className: 'border-warning/40 text-warning' },
      { variant: 'outline', tone: 'danger', className: 'border-danger/40 text-danger' },
    ],
    defaultVariants: {
      variant: 'solid',
      tone: 'neutral',
      size: 'sm',
    },
  },
);

export interface ChipProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof chipVariants> {}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(
  ({ className, variant, tone, size, ...props }, ref) => (
    <span ref={ref} className={cn(chipVariants({ variant, tone, size, className }))} {...props} />
  ),
);
Chip.displayName = 'Chip';

export { chipVariants };
